import axios, { AxiosError } from 'axios'
import axiosRetry from 'axios-retry'
import { IConfig } from '../interfaces'
import apiConfig from '../api-config'
import defaultConfig from '../consts/defaultConfig'

/**
 * service that helps us to obtain the webview config.
 * @public
 * @function
 * @name getConfig
 * @param {string} customer
 * @param {Function} callbackOnError
 * @param {object} options
 * @param {string | null} options.customerCode the session's `customerAlias`,
 * sent verbatim (COM-699): SCS compares it byte-exact against
 * `CustomerAssignment` and ignores it when the storefront's flag is off.
 * @param {number} options.retries overrides the axios-retry count.
 * @param {AbortSignal} options.signal cancels the request, retries included.
 */

const {
  basicConfig: { url },
} = apiConfig

const callRetries = 3
const callRetryDelay = () => 1000
const callRetryCondition = () => true

export const getConfig = (
  customer?: string,
  callbackOnError: () => void = () => {},
  options: {
    customerCode?: string | null
    retries?: number
    signal?: AbortSignal
  } = {},
): Promise<{ data: IConfig; error?: AxiosError }> => {
  if (url) {
    // No trim and no case change: the code is compared byte-exact server side.
    const customerCodeParam = options.customerCode
      ? `&customerCode=${encodeURIComponent(options.customerCode)}`
      : ''

    const urlConfig = `${url}${customer}?jwt=${process.env.REACT_APP_CONFIG_TOKEN}${customerCodeParam}`

    axiosRetry(axios, {
      retries: callRetries,
      retryDelay: callRetryDelay,
      retryCondition: callRetryCondition,
    })
    return axios
      .get(urlConfig, {
        headers: {
          'x-api-key': process.env.REACT_APP_API_KEY!,
        },
        // Per-request overrides; absent on the boot request, which keeps its
        // retries and has no deadline.
        ...(options.retries === undefined
          ? {}
          : { 'axios-retry': { retries: options.retries } }),
        ...(options.signal ? { signal: options.signal } : {}),
      })
      .then((response) => {
        const { data } = response
        const config = data?.data?.data || {}
        return { data: config }
      })
      .catch((error: AxiosError) => {
        return new Promise<{ data: IConfig; error?: AxiosError }>(
          (_, reject) => {
            reject(error)
            callbackOnError()
          },
        )
      })
  } else {
    return new Promise<{ data: IConfig; error?: AxiosError }>((resolve) => {
      resolve({ data: defaultConfig })
    })
  }
}

export default getConfig
